<template>
  <b-modal id="refill-balance-modal"
           v-if="patient"
           @hide="onHide"
           @show="onShow"
           :title="advanceForRefill ? $t('return') : $t('balance_refill')"
           centered
           hide-footer>

    <div class="row" v-if="!advanceForRefill">
      <div class="col">
        <label class="form-label">{{ $t('balance') }}</label>
        <div class="emphasized-text">
          {{ formatCurrency(patient.advance_calculated) }}
        </div>
      </div>
      <div class="col">
        <label class="form-label">{{ $t('debt') }}</label>
        <div class="emphasized-text text-danger">
          {{ formatCurrency(patient.debt_calculated) }}
        </div>
      </div>
    </div>

    <hr/>

    <div class="row align-items-end">

      <div class="col-6">
        <SelectGroup :options="finalPayMethods"
                     variant="white"
                     :label="$t('payment_type')"
                     :translate="true"
                     :showlabel="true"
                     v-model="pay_method"/>
        <ValidationError validationId="form"/>
      </div>

      <div class="col-6">
        <InputGroup type="number"
                    :label="$t('sum')"
                    :required="true"
                    v-model="amount"/>
        <ValidationError validationId="paid"/>
      </div>

      <div class="col-12"
           v-if="deposit_accounts.length > 1 && !advanceForRefill">
        <SelectGroup :options="deposit_accounts"
                     variant="white"
                     :label="$t('deposit_account')"
                     :translate="true"
                     :null-option="false"
                     :showlabel="true"
                     v-model="deposit_account_id"/>
      </div>
      <div class="col-12">
        <InputGroup :label="$t('comment')"
                    :required="false"
                    v-model="comment"/>
        <ValidationError validationId="comment"/>
      </div>
      <div class="col-12" v-if="canUseFiscalCheque">
        <div class="row align-items-center">
          <div class="col-12">
            <CheckboxGroup :label="$t('fiscal_cheque')"
                           :fid="`ppo-checkbox`"
                           v-model="sendCheque"/>
            <CheckboxGroup :label="$t('electronic_cheque')"
                           v-if="sendCheque"
                           :fid="`electronic_cheque`"
                           v-model="electronicCheque"/>
          </div>
          <div class="col-12" v-if="sendCheque && aqsiEnabled && aqsiAccounts.length">
            <SelectGroup :options="aqsiAccounts"
                         :null-option="true"
                         variant="white"
                         :label="$t('AQSI_account')"
                         :translate="false"
                         valuekey="key"
                         title="title"
                         :showlabel="true"
                         v-model="ppoAccount"/>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="form-group">
          <button v-if="advanceForRefill"
                  :disabled="disabled || loading"
                  @click="save('Y')"
                  class="pointer position-relative btn-themed btn-success btn-themed-squared btn-process d-block w-100">
            {{ $t('return') }}
          </button>
          <b-dropdown right variant="link" no-caret v-else>
            <template #button-content class="w-100">
              <button :disabled="disabled"
                      class="pointer position-relative btn-themed btn-success btn-themed-squared btn-process d-block w-100">
                {{ $t('process') }}
              </button>
            </template>
            <div class="pt-0">
              <button :disabled="loading"
                      @click="save('N')"
                      class="btn w-100 text-left">
                {{ $t('filling') }}
              </button>
              <button :disabled="loading"
                      @click="save('Y')"
                      class="btn w-100 text-left">
                {{ $t('write_off') }}
              </button>
            </div>
          </b-dropdown>
        </div>
      </div>
    </div>

    <KKMCardPayModal :modal-resolve="payModalAction"
                     :status="kkmAcquiringStatus"/>
  </b-modal>
</template>

<script setup>
import { useStore } from "vuex"
import InputGroup from "@/components/form/InputGroup"
import SelectGroup from "@/components/form/SelectGroup"
import { EloquentService } from "@/services/api/eloquent.api.service"
import ValidationError from "@/components/form/ValidationError"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import { formatCurrency } from "@/extensions/filters/filters"
import KKMService from "@/services/kkm.service"
import { appendFormdata } from "@/extensions/prototypes/prototypes"
import KKMCardPayModal from "@/components/parts/cashdesk/KKMCardPayModal.vue"

import {computed, getCurrentInstance, onBeforeMount, ref} from "vue"
const store = useStore()
const getters = store.getters

let i18n = null
let noty = null
let bvModal = null

const amount = ref(0)
const pay_method = ref(1)
const comment = ref('')
const ppoAccount = ref(null)
const sendCheque = ref(false)
const deposit_account_id = ref(null)
const electronicCheque = ref(false)
const deposit_accounts = ref([])
const payModalAction = ref(null)
const payMethodDetails = ref(null)
const kkmAcquiringStatus = ref(null)
const kkmCardPaymentId = ref(null)

const loading = computed(() => store.state.dom.loading)
const patient = computed(() => store.state.patient.patient)
const pay_methods = computed(() => store.state.auth.pay_methods)
const branch = computed(() => store.state.auth.branch)
const aqsiAccounts = computed(() => getters.aqsiAccounts)
const advanceForRefill = computed(() => store.state.patient.advanceForRefill)

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
  bvModal = instance?.ctx?._bv__modal
})
const aqsiEnabled = computed(() => getters.cashdeskSystemSelected === 'aqsi')
const finalPayMethods = computed(() => pay_methods.value.filter(x => ![5,100].includes(x.id))) // no deposit and family account
const canUseFiscalCheque = computed(() => getters.cashdeskSystemSelected != null)
const disabled = computed(() => {
  if (amount.value <= 0) return true
  if (sendCheque.value && !ppoAccount.value && aqsiEnabled.value) return true
  if (advanceForRefill.value && Number(amount.value) > Number(advanceForRefill.value.paid - (advanceForRefill.value.refunded_amount || 0))) return true
  return false
})

const save = async (cancel) => {
  try {
    let formData = new FormData()
    formData = appendFormdata(formData, {
      client_id: patient.value.id,
      cancel: cancel,
      form: pay_method.value,
      paid: amount.value,
      ppoAccount: ppoAccount.value,
      sendCheque: sendCheque.value ? 1 : 0,
      comment: comment.value,
      parent_advance_id: advanceForRefill.value?.advance_id || null,
      deposit_account_id: deposit_account_id.value
    })

    const res = await EloquentService.create('advance', formData)

    if(getters.kkmEnabled && sendCheque.value) {
      if((getters.dynamicConfigValue('kkm_acquiring_methods', branch.value) || []).includes(Number(pay_method.value))) {
        payMethodDetails.value = (await EloquentService.show('pay_method', pay_method.value))?.data?.custom_data || null
        kkmAcquiringStatus.value = null
        bvModal.show('kkm-card-pay-modal')
        let r
        if(advanceForRefill.value) {
          const universalId = advanceForRefill.value.meta_data?.kkmCardPaymentId || null
          r = await KKMService.returnPaymentByPaymentCard(Number(amount.value), payMethodDetails.value?.kkm_acquiring || 0, universalId, payMethodDetails.value?.kkm_inn || "")
        } else {
          r = await KKMService.payByPaymentCard(Number(amount.value), payMethodDetails.value?.kkm_acquiring || 0, payMethodDetails.value?.kkm_inn || "")
        }
        if(r.error) {
          noty.error(r.error.toString())
          kkmAcquiringStatus.value = 2
        } else {
          kkmAcquiringStatus.value = r.data.Status
          kkmCardPaymentId.value = r.data.UniversalID
        }
        bvModal.hide('kkm-card-pay-modal')
        const paidByCard = kkmAcquiringStatus.value === 0
        if(!paidByCard) return
      }
      await kkmPrintCheque(res.data)
    }
    await store.dispatch('getPatient', patient.value.id)
    bvModal.hide('refill-balance-modal')
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e?.response)
  }
}

const onHide = () => {
  amount.value = 0
  pay_method.value = 1
  comment.value = ''
  ppoAccount.value = null
  sendCheque.value = false
  store.commit('setAdvanceForRefill', null)
}

const onShow = async () => {
  const res = await EloquentService.dataTable('deposit_account')
  deposit_accounts.value = res.data?.result?.data || []
  if(deposit_accounts.value.length) {
    deposit_account_id.value = deposit_accounts.value.find(d => d.is_default === 1)?.id || deposit_accounts.value[0].id
  }
  if(advanceForRefill.value) {
    deposit_account_id.value = advanceForRefill.value.deposit_account_id
  }
}

const kkmPrintCheque = async (advance) => {
  const res = await KKMService.printCheque({
    totalPrice: Number(advance.paid),
    totalPaid: Number(advance.paid),
    payId: advance.advance_id,
    amount: Number(advance.paid),
    payMethod: Number(advance.form),
    services: [],
    numDevice: 0,
    transactionType: advance.cancel === 'Y' ? 'depositWithdrawal' : 'depositFill',
    email: electronicCheque.value ? patient.value.email : null
  })
  if(res?.error) {
    noty.error(i18n.t('error_printing_cheque') + ': ' + res.error)
  }
  if(res?.data?.CheckNumber) {
    const chequeId = res?.data?.CheckNumber
    let formData = new FormData()
    formData = appendFormdata(formData, {
      cheque_id: chequeId, // we only need to update cheque id, everything else is to pass validation
      client_id: advance.client_id,
      form: advance.form,
      paid: advance.paid,
      cancel: advance.cancel,
      date_timezone: advance.date_timezone,
      kkmCardPaymentId: kkmCardPaymentId.value
    })
    await EloquentService.update('advance', advance.advance_id, formData)
    kkmCardPaymentId.value = null
  }
}
</script>

<style scoped lang="scss">
.btn-success {
  height: 46px;
  line-height: 44px;
}
.dropdown {
  width: 100%;
}
@media screen and (max-width: 768px) {
  .btn-process {
    height: 32px;
    line-height: 30px;
  }
}
</style>
